import {useState, useCallback, useRef, useEffect} from 'react';
import {effectivePositionMode} from '../lib/position-mode';
import {tableNaturalSize} from '../lib/table-scale';
import {TableChrome} from './TableChrome';
import type {PhotoRequirement, RequirementOf} from '../lib/types';
import type {RichText} from '../lib/rich-text';
import {RichTextEditor} from './RichTextEditor';
import {RichTextView} from './RichTextView';
import {TableContextMenu} from './TableContextMenu';
import {CellElementView, CellElementInteractive} from './TableCellRenderers';
import {CellElementFill} from './TableCellFill';
import {cellFillKey, isFillableCellElement} from '../lib/cell-fill';
import {TableSizePicker} from './TableSizePicker';
import {useTableResize} from '../hooks/useTableResize';
import {useTableKeyboard} from '../hooks/useTableKeyboard';
import {useTableFillDrag} from '../hooks/useTableFillDrag';
import {CellFillOverlay} from './CellFillOverlay';
import {cellAtPoint} from '../lib/cell-at-point';
import {useCellEditorFocus} from '../hooks/useCellEditorFocus';
import {
  normalizeRange,
  isSingleCell,
  isCellInRange,
  singleRange,
  initTable,
  nextTabCell,
  buildCellStyle,
  cellItemStyle,
  toTableSnapshot,
} from '../lib/table-ops';
import {
  canDragResize,
  cellEditText,
  cellSelection,
  cellText,
  getCellElements,
  withCellText,
} from '../lib/cell-elements';
import {labelText, withLabelText} from '../lib/label-text';
import {cellTextFormat} from '../lib/text-format';
import {MIN_COL_WIDTH, DEFAULT_ROW_HEIGHT, tableWrapperStyle} from '../lib/table-types';
import {FillSlotProps} from './renderers/element-props';
import {CELL_SCAFFOLDING, CellStackItem, EditorCellElement} from './TableCellAuthoring';
import type {
  CellElement,
  CellRange,
  TableCellData,
  TableConfig,
  TableConfigSnapshot,
  TableRowData,
} from '../lib/table-types';

// Re-export the data model + operations so existing importers of "./TableElement"
// keep working. Implementations now live in lib/table-types and lib/table-ops.
// eslint-disable-next-line react-refresh/only-export-components
export * from '../lib/table-ops';
// eslint-disable-next-line react-refresh/only-export-components
export * from '../lib/cell-elements';
// eslint-disable-next-line react-refresh/only-export-components
export * from '../lib/table-types';

export interface TableElementProps {
  element: RequirementOf<'table'>;
  onChange: (updated: PhotoRequirement) => void;
  onSelect?: () => void;
  /**
   * Variable context for {{token}} resolution in cell content + cell-element
   * labels. Empty in the editor (tokens stay literal); populated in the job
   * completion report so cells show real values, aligned with elements outside
   * the table.
   */
  sampleData?: Record<string, string>;
  /**
   * Render a plain, non-interactive table — no selection highlight, resize
   * handles, expand dots, drag grips, context menu or cell editing. Used by the
   * job completion report (fill / read-only), where the table structure is fixed.
   *
   * NOTE: read-only refers to the table's *structure*, not its cells. When
   * `cellFill` is wired the cells are still live inputs — see below.
   */
  readOnly?: boolean;
  /**
   * Interactive fill for the elements INSIDE the cells (DERP-225).
   *
   * A table is a container: its cells are the fill slots, so unlike every other
   * element it can't work from a single pre-scoped `fillValue`/`onFillChange`
   * pair. It is handed `LivePreview`'s scoping function itself and calls it per
   * cell with that cell's `cellFillKey`, so a cell is wired exactly the way a
   * standalone element is. Absent = the previous behaviour, a display-only
   * table.
   */
  cellFill?: FillSlotProps;
}

/**
 * Which element of the pressed cell the press landed on, or `null` for the cell
 * around them. Read off the DOM at the cell's own mousedown, which is the one
 * handler that both selects and tells a first click from a second, so nothing
 * has to be handed to it by an earlier handler.
 */
function pressedElementIndex(e: React.MouseEvent): number | null {
  const hit = (e.target as Element).closest<HTMLElement>('[data-el-idx]');
  return hit && e.currentTarget.contains(hit) ? Number(hit.dataset.elIdx) : null;
}

export function TableElement({
  element,
  onChange,
  onSelect,
  sampleData,
  readOnly = false,
  cellFill,
}: TableElementProps) {
  const ctx = sampleData ?? {};
  const raw = element.config;
  const rows = raw.rows ?? [];
  const columns = raw.columns ?? [];
  const cellRange = raw.cellRange ?? null;
  const selectedElementIndex: number | null = raw.selectedElementIndex ?? null;

  // ── Cell editing ─────────────────────────────────────────────────────────────
  const [editingCell, setEditingCell] = useState<{row: number; col: number} | null>(null);
  const [draft, setDraft] = useState<RichText>({text: ''});

  // ── Cell drag selection ───────────────────────────────────────────────────────
  const [liveDragRange, setLiveDragRange] = useState<CellRange | null>(null);
  const latestDragFocus = useRef<{row: number; col: number} | null>(null);

  // ── Context menu ──────────────────────────────────────────────────────────────
  const [ctxMenu, setCtxMenu] = useState<{x: number; y: number; ri: number; ci: number} | null>(
    null,
  );

  const firstRowCellRefs = useRef<Map<number, HTMLTableCellElement>>(new Map());
  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map());

  // ── Stable refs ───────────────────────────────────────────────────────────────
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const {openEditor, placeCaret, focusWrapper} = useCellEditorFocus({
    tableWrapperRef,
    setEditingCell,
    setDraft,
  });

  const onChangeRef = useRef(onChange);
  const elementRef = useRef(element);
  const onSelectRef = useRef(onSelect);

  const configRef = useRef<TableConfigSnapshot>(toTableSnapshot(raw));

  const editRef = useRef<{cell: {row: number; col: number} | null; draft: RichText}>({
    cell: null,
    draft: {text: ''},
  });

  // Keep latest-value refs in sync after each commit so event handlers, rAF
  // callbacks, and native listeners read current props/state without re-subscribing.
  useEffect(() => {
    onChangeRef.current = onChange;
    elementRef.current = element;
    onSelectRef.current = onSelect;
    configRef.current = toTableSnapshot(raw);
    editRef.current = {cell: editingCell, draft};
  });

  // ── Config helpers ────────────────────────────────────────────────────────────
  const toCfg = useCallback(
    (overrides: Partial<TableConfig>): TableConfig => ({
      ...configRef.current,
      ...overrides,
    }),
    [],
  );

  const update = useCallback((cfg: TableConfig) => {
    const el = elementRef.current;
    // **An absolute table carries its box with it.** The canvas renders an
    // absolute element at a fixed `width`/`height`, so a table that resized only
    // its columns and rows redrew inside a box that had not moved. Width from
    // the columns (what the drag just set); height MEASURED, because a stored
    // row height is a minimum — see `renderedRowHeight`.
    const box =
      effectivePositionMode(el) === 'absolute'
        ? {
            width: tableNaturalSize(cfg).width,
            height: tableWrapperRef.current?.offsetHeight || tableNaturalSize(cfg).height,
          }
        : null;
    const next = {...el, config: cfg, ...(box ?? {})};
    // Reflect the commit in the latest-value refs IMMEDIATELY, before handing it
    // up. They are otherwise only refreshed by the post-render effect below, so
    // two update() calls in the SAME TICK both read pre-commit state through
    // `toCfg` and the second silently discards the first.
    //
    // That is not hypothetical: choosing a cell photo's logo writes the config,
    // then closing the library restores focus into the cell and the selection
    // update that follows rebuilt from the stale ref — writing the logo and
    // overwriting it in one gesture, so the chosen logo never appeared
    // (DERP-225).
    elementRef.current = next;
    configRef.current = toTableSnapshot(cfg);
    onChangeRef.current(next);
  }, []);

  // Drag/resize interactions (columns, rows, whole table, cell elements) + their
  // transient draft-sizing state live in a dedicated hook.
  const {
    draftWidths,
    draftHeights,
    draftTableWidth,
    draftElementSize,
    startColResize,
    startRowResize,
    startCellElementResize,
    startOuterResize,
  } = useTableResize({configRef, toCfg, update, firstRowCellRefs, rowRefs});

  // Alignment is a flow question; see `tableAlignStyle`.
  const isAbsolute = effectivePositionMode(element) === 'absolute';

  // The comfortable default, a drag-set value, or the in-drag draft. All are
  // minimums per HTML table semantics — content always renders in full.
  const rowHeights = draftHeights ?? rows.map((r) => r.height ?? DEFAULT_ROW_HEIGHT);

  // ── Keyboard handler (fires when wrapper has focus, not when textarea is active) ──
  const handleTableKeyDown = useTableKeyboard({
    configRef,
    toCfg,
    update,
    editRef,
    setEditingCell,
    setDraft,
    tableWrapperRef,
  });

  // ── Fill handle (drag the selection's corner to copy it down or right) ─────────
  const fill = useTableFillDrag({
    configRef,
    toCfg,
    update,
    rows,
    selection: liveDragRange || editingCell ? null : cellRange,
  });

  // ── Cell drag selection ────────────────────────────────────────────────────────
  const startCellDrag = useCallback(
    (ri: number, ci: number, e: React.MouseEvent) => {
      if (e.button !== 0) return;
      e.stopPropagation();
      e.preventDefault();
      onSelectRef.current?.();
      const pressed = pressedElementIndex(e);

      // Second-click-to-edit: if this cell is already the single selection and the
      // press is on its selected element (or on the cell around it), enter edit
      // mode instead of re-selecting. A press on a different element of the cell
      // only selects that element -- the next press edits it.
      if (!editRef.current.cell) {
        const existingRange = configRef.current.cellRange;
        if (existingRange) {
          const nr = normalizeRange(existingRange);
          if (isSingleCell(nr) && nr.startRow === ri && nr.startCol === ci) {
            const {element: activeEl, index} = cellSelection(configRef.current, ri, ci);
            if (activeEl && (pressed === null || pressed === index)) {
              openEditor({row: ri, col: ci}, labelText(activeEl), e);
              return;
            }
          }
        }
      }

      const {cell: ec, draft: d} = editRef.current;
      const anchor = {row: ri, col: ci};
      const initialRange = singleRange(ri, ci);
      const pressedSelection = pressed === null ? {} : {selectedElementIndex: pressed};
      latestDragFocus.current = anchor;

      if (ec) {
        const committedRows = configRef.current.rows.map((row, rIdx) =>
          rIdx !== ec.row
            ? row
            : {
                ...row,
                cells: row.cells.map((cell, cIdx) => {
                  if (cIdx !== ec.col) return cell;
                  const {stack, index: activeIdx} = cellSelection(
                    configRef.current,
                    ec.row,
                    ec.col,
                  );
                  if (stack.length > 0) {
                    return {
                      ...cell,
                      elements: stack.map((el, i) => (i === activeIdx ? withLabelText(el, d) : el)),
                      element: undefined,
                    };
                  }
                  return withCellText(cell, d);
                }),
              },
        );
        configRef.current.rows = committedRows;
        update(toCfg({rows: committedRows, cellRange: initialRange, ...pressedSelection}));
        setEditingCell(null);
      } else {
        update(toCfg({cellRange: initialRange, ...pressedSelection}));
      }

      configRef.current.cellRange = initialRange;
      setLiveDragRange(initialRange);
      document.body.style.userSelect = 'none';

      const onMove = (me: MouseEvent) => {
        const hit = cellAtPoint(me.clientX, me.clientY);
        if (!hit) return;
        latestDragFocus.current = hit;
        setLiveDragRange({
          startRow: anchor.row,
          startCol: anchor.col,
          endRow: hit.row,
          endCol: hit.col,
        });
      };

      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        document.body.style.userSelect = '';
        const focus = latestDragFocus.current ?? anchor;
        latestDragFocus.current = null;
        setLiveDragRange(null);
        const finalRange: CellRange = {
          startRow: anchor.row,
          startCol: anchor.col,
          endRow: focus.row,
          endCol: focus.col,
        };
        update(toCfg({cellRange: finalRange}));
        // Focus wrapper for keyboard nav (so Delete removes the selected element,
        // not the whole table), unless the double-click this press begins opens
        // an editor first.
        focusWrapper();
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [update, toCfg, openEditor, focusWrapper],
  );

  // ── Read-only / preview render ─────────────────────────────────────────────────
  // A structurally static table: no selection, resize handles, expand dot, drag
  // grips, context menu, or empty-cell "·" placeholder. Used in the job
  // completion report.
  //
  // The CELLS are still live when `cellFill` is wired (DERP-225): each fillable
  // cell element renders through `CellElementFill`, which delegates to the same
  // renderers as elements outside the table, so a crew fills a cell exactly the
  // way they fill anything else. Header rows stay display-only — they label the
  // table rather than collect an answer.
  if (readOnly) {
    if (rows.length === 0) return null;
    return (
      <div style={{width: raw.tableWidth ? `${raw.tableWidth}px` : '100%', maxWidth: '100%'}}>
        <table className="border-collapse table-fixed" style={{width: '100%'}}>
          <colgroup>
            {columns.map((col) => (
              <col key={col.id} style={col.width !== undefined ? {width: `${col.width}px`} : {}} />
            ))}
          </colgroup>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className={row.isHeader ? 'bg-slate-50' : undefined}
                // Canva-style sizing (DERP-226): the height — the comfortable
                // default or a drag-set value — is a MINIMUM per HTML table
                // semantics, so content always renders in full, never clipped.
                style={{height: `${row.height ?? DEFAULT_ROW_HEIGHT}px`}}
              >
                {row.cells.map((cell, ci) => {
                  if (cell.hidden) return null;
                  const {td: tdStyle, text: textStyle} = buildCellStyle(cell.style);
                  const finalTextStyle: React.CSSProperties =
                    row.isHeader && cell.style?.bold === undefined
                      ? {...textStyle, fontWeight: '600'}
                      : textStyle;
                  const cellElements = getCellElements(cell);
                  return (
                    <td
                      key={cell.id}
                      rowSpan={cell.rowSpan}
                      colSpan={cell.colSpan}
                      // No scaffolding grid here. The editor's dotted border is a
                      // design guide -- it shows the author where the cells are while
                      // there is nothing in them. A finished report has no such
                      // question, so an unset side draws no line at all, and the only
                      // rules on the page are the ones the author asked for. The PDF
                      // rasterises this very DOM, so the export follows.
                      className="align-top"
                      style={{...tdStyle, minWidth: columns[ci]?.minWidth ?? MIN_COL_WIDTH}}
                    >
                      {cellElements.length > 0 ? (
                        <div className="block">
                          {cellElements.map((el, elIdx) => {
                            const key = cellFillKey(element.id, row.id, cell.id, elIdx);
                            return (
                              <CellStackItem
                                key={elIdx}
                                el={el}
                                cellStyle={cell.style}
                                last={elIdx === cellElements.length - 1}
                              >
                                {cellFill && !row.isHeader && isFillableCellElement(el) ? (
                                  <CellElementFill
                                    element={el}
                                    cellKey={key}
                                    cellStyle={cell.style}
                                    sampleData={ctx}
                                    {...cellFill(key)}
                                  />
                                ) : (
                                  <CellElementView
                                    element={el}
                                    sampleData={ctx}
                                    textStyle={finalTextStyle}
                                  />
                                )}
                              </CellStackItem>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="whitespace-pre-wrap block" style={finalTextStyle}>
                          <RichTextView value={cellText(cell)} sampleData={ctx} />
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // ── Size-picker guard ─────────────────────────────────────────────────────────
  if (rows.length === 0) {
    return (
      <TableSizePicker
        onSelect={(r, c) => {
          update(initTable(r, c));
          onSelectRef.current?.();
        }}
      />
    );
  }

  // ── Cell editing helpers ──────────────────────────────────────────────────────
  const buildCommittedRows = (ri: number, ci: number, text: RichText): TableRowData[] =>
    rows.map((row, rIdx) =>
      rIdx !== ri
        ? row
        : {
            ...row,
            cells: row.cells.map((cell, cIdx) => (cIdx !== ci ? cell : withCellText(cell, text))),
          },
    );

  const commitCurrentEdit = (ri: number, ci: number): TableRowData[] => {
    const {stack, index: activeIdx} = cellSelection({...raw, rows}, ri, ci);
    if (stack.length > 0) {
      return rows.map((row, rIdx) =>
        rIdx !== ri
          ? row
          : {
              ...row,
              cells: row.cells.map((cell, cIdx) => {
                if (cIdx !== ci) return cell;
                return {
                  ...cell,
                  elements: getCellElements(cell).map((el, i) =>
                    i === activeIdx ? withLabelText(el, draft) : el,
                  ),
                  element: undefined,
                };
              }),
            },
      );
    }
    return buildCommittedRows(ri, ci, draft);
  };

  const finishEdit = (ri: number, ci: number, restoreFocus = false) => {
    update(toCfg({rows: commitCurrentEdit(ri, ci)}));
    setEditingCell(null);
    if (restoreFocus) focusWrapper();
  };

  /**
   * Pin the cell range to one cell and select one of its elements.
   *
   * The one write that moves the selection, so the two gestures that move it
   * cannot disagree about what moving it means. `rows` rides along when the
   * caller is also committing an edit: one `update`, because a commit and the
   * switch it causes have to land together — `toCfg` reads a snapshot, so two
   * calls in the same tick would see the same pre-commit state and the second
   * would discard the first.
   */
  const selectElement = (ri: number, ci: number, elIdx: number, rows?: TableRowData[]) =>
    update(
      toCfg({
        ...(rows ? {rows} : {}),
        cellRange: singleRange(ri, ci),
        selectedElementIndex: elIdx,
      }),
    );

  /**
   * Move the open editor to another element in the same cell.
   *
   * Commits before it moves, the way Tab does when it carries the edit to the
   * next cell: the draft belongs to the element being left. The editor's own
   * blur commits too, so the text was never at risk — what was missing is the
   * half that moves the SELECTION, which is what the next double-click reads.
   */
  const moveEditTo = (ri: number, ci: number, elIdx: number) => {
    const rows = commitCurrentEdit(ri, ci);
    selectElement(ri, ci, elIdx, rows);
    const moved = cellSelection({rows, selectedElementIndex: elIdx}, ri, ci).element;
    setDraft(moved ? labelText(moved) : {text: ''});
  };

  const handleTab = (ri: number, ci: number, e: React.KeyboardEvent) => {
    e.preventDefault();
    const newRows = commitCurrentEdit(ri, ci);
    const next = nextTabCell(rows, ri, ci, e.shiftKey);
    if (!next) {
      update(toCfg({rows: newRows}));
      setEditingCell(null);
      focusWrapper();
      return;
    }
    const nextCell = rows[next.row]?.cells[next.col];
    update(toCfg({rows: newRows, cellRange: singleRange(next.row, next.col)}));
    setEditingCell({row: next.row, col: next.col});
    setDraft(cellEditText(nextCell, nextCell && getCellElements(nextCell)[0]));
  };

  /**
   * One cell element as the AUTHOR sees it, at the address it lives at.
   *
   * Every prop `EditorCellElement` takes is derived from WHERE the element is,
   * so it is derived once, here. The editor draws a cell element in three
   * places — beside the one being edited, selected with a resize corner, and
   * plain — and each spelled the same six props out by hand; a fourth would have
   * arrived spelling one of them differently, which is the drift this whole
   * change is about, one level in.
   *
   * The live drag size is folded in here too. It was tested twice per call site,
   * once for each axis, in two copies of the same three-way comparison.
   */
  const authoredElement = (at: {
    ri: number;
    ci: number;
    elIdx: number;
    row: TableRowData;
    cell: TableCellData;
    el: CellElement;
    textStyle: React.CSSProperties;
  }) => {
    const dragging =
      draftElementSize?.ri === at.ri &&
      draftElementSize?.ci === at.ci &&
      draftElementSize.elementIdx === at.elIdx;
    return (
      <EditorCellElement
        element={at.el}
        cellKey={cellFillKey(element.id, at.row.id, at.cell.id, at.elIdx)}
        isHeaderRow={at.row.isHeader === true}
        cellStyle={at.cell.style}
        sampleData={ctx}
        textStyle={at.textStyle}
        overrideH={dragging ? draftElementSize.h : undefined}
        overrideW={dragging ? draftElementSize.w : undefined}
      />
    );
  };

  const displayRange = liveDragRange ?? cellRange;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div
      ref={tableWrapperRef}
      tabIndex={0}
      data-table-wrapper=""
      className="outline-none relative group/table"
      style={tableWrapperStyle(raw, draftTableWidth, isAbsolute)}
      onKeyDown={handleTableKeyDown}
      onMouseDown={(e) => {
        // **The one event the table does not swallow.** Every other press is
        // stopped here so it means "work in this cell" rather than "drag this
        // element" — which is also why a table could not be moved at all. The
        // grip's press passes through to the canvas's own drag, so a table moves
        // by whichever mechanism its position mode already uses.
        if ((e.target as HTMLElement).closest('[data-table-move]')) return;
        e.stopPropagation();
      }}
    >
      <table className="border-collapse table-fixed" style={{width: '100%'}}>
        <colgroup>
          {columns.map((col, ci) => {
            const w = draftWidths ? draftWidths[ci] : col.width;
            return <col key={col.id} style={w !== undefined ? {width: `${w}px`} : {}} />;
          })}
        </colgroup>
        <tbody>
          {rows.map((row, ri) => (
            <tr
              key={row.id}
              ref={(el) => {
                if (el) rowRefs.current.set(ri, el);
                else rowRefs.current.delete(ri);
              }}
              className={row.isHeader ? 'bg-slate-50' : undefined}
              style={{height: `${rowHeights[ri] ?? DEFAULT_ROW_HEIGHT}px`}}
            >
              {row.cells.map((cell, ci) => {
                if (cell.hidden) return null;
                const isEditing = editingCell?.row === ri && editingCell?.col === ci;
                const inRange = displayRange ? isCellInRange(displayRange, ri, ci) : false;
                const isSingle =
                  inRange && displayRange != null
                    ? isSingleCell(normalizeRange(displayRange))
                    : false;
                const {td: tdStyle, text: textStyle} = buildCellStyle(cell.style);
                // What the cell's words inherit, for the toolbar to read a
                // selection against (DERP-307).
                const cellBase = cellTextFormat(cell.style, row.isHeader === true);

                // Header rows default to bold when no explicit bold style is set.
                const finalTextStyle: React.CSSProperties =
                  row.isHeader && cell.style?.bold === undefined
                    ? {...textStyle, fontWeight: '600'}
                    : textStyle;

                const outline = isEditing
                  ? '2px solid #6366f1'
                  : isSingle && !isEditing
                    ? '2px solid #818cf8'
                    : undefined;

                return (
                  <td
                    key={cell.id}
                    data-ri={ri}
                    data-ci={ci}
                    ref={(el) => {
                      if (ri === 0) {
                        if (el) firstRowCellRefs.current.set(ci, el);
                        else firstRowCellRefs.current.delete(ci);
                      }
                    }}
                    rowSpan={cell.rowSpan}
                    colSpan={cell.colSpan}
                    className={`border cursor-text relative ${CELL_SCAFFOLDING}`}
                    style={{
                      ...tdStyle,
                      minWidth: columns[ci]?.minWidth ?? MIN_COL_WIDTH,
                      outline,
                      outlineOffset: outline ? '-1px' : undefined,
                    }}
                    onMouseDown={(e) => {
                      if (isEditing) return;
                      startCellDrag(ri, ci, e);
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // If right-clicked outside the current selection, move selection here.
                      const nr = configRef.current.cellRange
                        ? normalizeRange(configRef.current.cellRange)
                        : null;
                      if (!nr || !isCellInRange(nr, ri, ci)) {
                        update(toCfg({cellRange: singleRange(ri, ci)}));
                      }
                      setCtxMenu({x: e.clientX, y: e.clientY, ri, ci});
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      if (isEditing) return;
                      const currentCell = configRef.current.rows[ri]?.cells[ci];
                      const {element} = cellSelection(configRef.current, ri, ci);
                      openEditor({row: ri, col: ci}, cellEditText(currentCell, element), e);
                    }}
                  >
                    {/* Multi-cell range highlight */}
                    {inRange && !isSingle && !isEditing && (
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{backgroundColor: 'rgba(99, 102, 241, 0.12)'}}
                      />
                    )}

                    <CellFillOverlay fill={fill} ri={ri} ci={ci} inRange={inRange} />

                    {(() => {
                      const cellElements = getCellElements(cell);

                      if (isEditing && cellElements.length > 0) {
                        return (
                          <div
                            className="relative z-[1]"
                            onClick={(e) => e.stopPropagation()}
                            onDoubleClick={(e) => e.stopPropagation()}
                          >
                            {cellElements.map((el, elIdx) => {
                              const isActiveEl = (selectedElementIndex ?? 0) === elIdx;
                              return (
                                <CellStackItem
                                  key={elIdx}
                                  el={el}
                                  cellStyle={cell.style}
                                  last={elIdx === cellElements.length - 1}
                                >
                                  {isActiveEl ? (
                                    <CellElementInteractive
                                      element={el}
                                      editorRef={placeCaret}
                                      draft={draft}
                                      onDraftChange={setDraft}
                                      onFinish={(restoreFocus) => finishEdit(ri, ci, restoreFocus)}
                                      onCancel={() => {
                                        setEditingCell(null);
                                        focusWrapper();
                                      }}
                                      onTab={(e) => handleTab(ri, ci, e)}
                                      textStyle={finalTextStyle}
                                      owner={element.id}
                                      base={cellBase}
                                    />
                                  ) : (
                                    // Editing one element must not make its
                                    // neighbours unreachable. This branch drew
                                    // them with no handler of their own, under a
                                    // wrapper that stops every click -- so once a
                                    // cell was in edit mode, clicking the element
                                    // below did nothing, and double-clicking it
                                    // re-read the OLD `selectedElementIndex` and
                                    // re-opened the element already being edited.
                                    // From the outside that reads as "the click
                                    // was ignored", which is exactly what it was.
                                    <div
                                      // Mousedown, for the same reason as the
                                      // non-editing branch below: the commit this
                                      // triggers re-renders the stack, and a click
                                      // dispatched afterwards lands on a node that
                                      // is no longer in the document.
                                      //
                                      // `preventDefault` holds focus in the editor
                                      // being left, which stops its blur firing a
                                      // SECOND commit -- that one would run after
                                      // the switch and write this element's draft
                                      // into the element just moved to.
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        moveEditTo(ri, ci, elIdx);
                                      }}
                                    >
                                      {authoredElement({
                                        ri,
                                        ci,
                                        elIdx,
                                        row,
                                        cell,
                                        el,
                                        textStyle: finalTextStyle,
                                      })}
                                    </div>
                                  )}
                                </CellStackItem>
                              );
                            })}
                          </div>
                        );
                      }

                      if (isEditing) {
                        return (
                          <RichTextEditor
                            ref={placeCaret}
                            value={draft}
                            onChange={setDraft}
                            owner={element.id}
                            base={cellBase}
                            className="w-full bg-transparent block relative z-[1]"
                            style={finalTextStyle}
                            onBlurAway={() => finishEdit(ri, ci)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                finishEdit(ri, ci, true);
                              }
                              if (e.key === 'Escape') {
                                e.preventDefault();
                                setEditingCell(null);
                                focusWrapper();
                              }
                              if (e.key === 'Tab') handleTab(ri, ci, e);
                            }}
                          />
                        );
                      }

                      if (cellElements.length > 0) {
                        return (
                          <div className="block select-text relative z-[1]">
                            {cellElements.map((el, elIdx) => {
                              const isActiveEl = isSingle && (selectedElementIndex ?? 0) === elIdx;
                              const canResize = canDragResize(el);
                              return (
                                <div
                                  key={elIdx}
                                  className={[
                                    'relative group/cel-el',
                                    elIdx < cellElements.length - 1 ? 'mb-1' : '',
                                    isActiveEl
                                      ? 'ring-1 ring-indigo-400 ring-offset-1 rounded'
                                      : '',
                                  ]
                                    .filter(Boolean)
                                    .join(' ')}
                                  // No mousedown of its own: the cell's
                                  // (`startCellDrag`) reads this index off the
                                  // press target and does the selecting. It has to
                                  // be the one handler, because it must see the
                                  // selection as it was before the press to tell a
                                  // first click from a second -- an element that
                                  // selected itself first made every first click
                                  // a second one. Selecting on mousedown rather
                                  // than click also matters: the first re-render
                                  // after mount replaces these nodes, and a click
                                  // whose node is gone by mouseup is lost.
                                  data-el-idx={elIdx}
                                  // Still swallowed, so the click does not reach the
                                  // canvas behind the table and deselect it.
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {/* No drag grip. It reserved a 20px gutter the
                                      moment a cell holding two elements was
                                      selected, so selecting a cell visibly
                                      narrowed everything in it -- the element
                                      moved and resized because you LOOKED at it.
                                      A cell is also the worst place to aim a
                                      drag: the grip and the resize corner sat
                                      within a few pixels of each other, and of
                                      the logo picker that used to be here too,
                                      in a box a couple of centimetres wide.
                                      Reordering lives in the cell's right-click
                                      menu instead, where it costs the element no
                                      room at all. */}
                                  <div style={cellItemStyle(cell.style, el)}>
                                    {canResize ? (
                                      <div className="relative group/elresize">
                                        {authoredElement({
                                          ri,
                                          ci,
                                          elIdx,
                                          row,
                                          cell,
                                          el,
                                          textStyle: finalTextStyle,
                                        })}
                                        <div
                                          className="absolute bottom-0 right-0 w-3 h-3 bg-indigo-400 rounded-tl opacity-0 group-hover/elresize:opacity-100 transition-opacity"
                                          style={{cursor: 'nwse-resize'}}
                                          onMouseDown={(e) =>
                                            startCellElementResize(ri, ci, elIdx, e)
                                          }
                                        />
                                      </div>
                                    ) : (
                                      authoredElement({
                                        ri,
                                        ci,
                                        elIdx,
                                        row,
                                        cell,
                                        el,
                                        textStyle: finalTextStyle,
                                      })
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      }

                      return (
                        <span
                          // Empty cells keep a one-line click target in the editor;
                          // filled cells fit their text exactly (DERP-226).
                          className={`whitespace-pre-wrap block select-text relative z-[1] ${cell.content ? '' : 'min-h-[1.25rem]'}`}
                          style={finalTextStyle}
                        >
                          {cell.content ? (
                            <RichTextView value={cellText(cell)} sampleData={ctx} />
                          ) : (
                            <span className="text-gray-300 select-none text-sm">·</span>
                          )}
                        </span>
                      );
                    })()}

                    {/* Every row and column, the last of each included: the
                        table's bottom line is the last row's bottom edge. Both
                        used to stop one short, because a drag took its size out
                        of the NEXT row or column and the last has no neighbour
                        to take from. A drag now moves only its own edge. */}
                    <div
                      data-col-edge={ci}
                      className="absolute inset-y-0 right-0 hover:bg-indigo-400/40 transition-colors"
                      style={{
                        width: 6,
                        transform: 'translateX(50%)',
                        cursor: 'col-resize',
                        zIndex: 10,
                      }}
                      onMouseDown={(e) => startColResize(ci, e)}
                    />
                    <div
                      data-row-edge={ri}
                      className="absolute inset-x-0 bottom-0 hover:bg-indigo-400/40 transition-colors"
                      style={{
                        height: 6,
                        transform: 'translateY(50%)',
                        cursor: 'row-resize',
                        zIndex: 10,
                      }}
                      onMouseDown={(e) => startRowResize(ri, e)}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <TableChrome
        wrapperRef={tableWrapperRef}
        element={element}
        readOnly={!!readOnly}
        onGripPress={() => {
          // **The grip selects the TABLE.** Every other press selects something
          // inside it and that selection persists, so pressing the grip with a
          // cell selected dragged the table while the toolbar still formatted
          // the cell. Written only when there IS a selection to clear — a press
          // that changes nothing must not reach the undo stack.
          onSelectRef.current?.();
          const {cellRange: range, selectedElementIndex: index} = configRef.current;
          if (range || index != null) {
            update(toCfg({cellRange: null, selectedElementIndex: null}));
          }
        }}
        startColResize={startColResize}
        startRowResize={startRowResize}
        startOuterResize={startOuterResize}
      />

      {ctxMenu && (
        <TableContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          ri={ctxMenu.ri}
          ci={ctxMenu.ci}
          // Spread `raw`, do not rebuild it. Naming three fields by hand
          // silently dropped `selectedElementIndex`, so the menu's Move up /
          // Move down read `undefined`, fell back to 0 and reordered the FIRST
          // element in the cell however many were stacked and whichever one the
          // author had selected. The three below stay because they carry the
          // render's defaults for an absent field; everything else comes along.
          cfg={{...raw, rows, columns, cellRange}}
          onUpdate={update}
          onClose={() => setCtxMenu(null)}
        />
      )}
    </div>
  );
}
